import { useState } from "react";
import type { MacroDocConfig } from "../scenario/types";
import { WindowTitlebar } from "./WindowTitlebar";
import { tx } from "../lib/tx";

/**
 * A Word-style macro document the victim opened from the lure landing. A yellow
 * "SECURITY WARNING — macros disabled" banner pressures the victim into clicking
 * "Enable Content" — which detonates an in-memory payload (App records
 * `enabled_macro`). The prize-claim form then "unlocks"; while the victim fills
 * it, a hacker-foothold cue appears. Screen-app (blends on the monitor).
 * completeOn `continue`.
 */
export function MacroDoc({
  config,
  onEnableMacro,
  onContinue,
}: {
  config: MacroDocConfig;
  onEnableMacro: () => void;
  onContinue: () => void;
}) {
  const [enabled, setEnabled] = useState(false);
  const [foothold, setFoothold] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});

  const [autofilled, setAutofilled] = useState(false);

  const enable = () => {
    if (enabled) return;
    setEnabled(true);
    onEnableMacro();
    // the payload "phones home" a beat after the macro runs.
    window.setTimeout(() => setFoothold(true), 900);
  };

  // One-click "fill every field" so the learner isn't stuck wondering whether they
  // must type each field (uses the configured value, else the example placeholder).
  const autofill = () => {
    const next: Record<string, string> = {};
    config.fields.forEach((f) => {
      next[f.id] = f.value ?? f.placeholder ?? "\u2014";
    });
    setValues(next);
    setAutofilled(true);
  };

  return (
    <div className="winapp macrodoc">
      <WindowTitlebar title={`${config.fileName ?? "Prize_Claim_Form.docm"} — ${config.appName ?? "Word"}`} />

      {/* faux ribbon */}
      <div className="md-ribbon">
        <span className="md-tab active">{tx("Home")}</span>
        <span className="md-tab">{tx("Insert")}</span>
        <span className="md-tab">{tx("Layout")}</span>
        <span className="md-tab">{tx("References")}</span>
        <span className="md-tab">{tx("Review")}</span>
        <span className="md-tab">{tx("View")}</span>
      </div>

      {/* macro security warning banner */}
      {!enabled && (
        <div className="md-banner">
          <span className="md-banner-ico" aria-hidden>{"\u26A0"}</span>
          <span className="md-banner-text">
            {config.bannerText ??
              tx("SECURITY WARNING  Macros have been disabled. This document cannot be saved until content is enabled.")}
          </span>
          <button type="button" className="md-enable" onClick={enable}>
            {config.enableLabel ?? tx("Enable Content")}
          </button>
        </div>
      )}

      <div className="md-page-wrap">
        <div className="md-page">
          {config.heading && <h1 className="md-heading">{config.heading}</h1>}
          <div className="md-body">
            {config.body.split(/\n{2,}/).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          {!enabled ? (
            <div className="md-locked">
              <span className="md-locked-ico" aria-hidden>{"\u{1F512}"}</span>
              <span>{tx("Enable content above to load the claim form.")}</span>
            </div>
          ) : (
            <div className="md-form">
              {!submitted && (
                <div className="md-autofill-row">
                  <button
                    type="button"
                    className={`md-autofill ${autofilled ? "" : "go"}`}
                    onClick={autofill}
                  >
                    {"\u2728"} {config.autofillLabel ?? tx("Autofill my details")}
                  </button>
                  <span className="md-autofill-hint">
                    {autofilled
                      ? tx("Fields filled — click Submit claim below.")
                      : tx("Click to fill the form, then Submit claim below.")}
                  </span>
                </div>
              )}
              {config.fields.map((f) => (
                <label key={f.id} className="md-field">
                  <span className="md-field-label">{f.label}</span>
                  <input
                    type="text"
                    placeholder={f.placeholder}
                    value={values[f.id] ?? ""}
                    onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))}
                    disabled={submitted}
                  />
                </label>
              ))}
              {submitted && (
                <div className="md-submitted">{"\u2713"} {tx("Details saved to the document.")}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* attacker foothold cue (corner) */}
      {foothold && (
        <div className="md-foothold">
          <span className="md-foothold-dot" />
          {config.footholdNote ?? tx("macro executed · in-memory implant active · beacon established")}
        </div>
      )}

      {/* Sticky action bar: the Submit / Continue button is ALWAYS pinned to the
          bottom of the window so it can never be scrolled off / cut off on a short
          monitor pane (the form fields scroll above it). */}
      {enabled && (
        <div className="md-action-bar">
          {!submitted ? (
            <button type="button" className="btn md-submit" onClick={() => setSubmitted(true)}>
              {config.submitLabel ?? tx("Submit claim")}
            </button>
          ) : (
            <button type="button" className="btn md-continue" onClick={onContinue}>
              {config.continueLabel ?? tx("Continue")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
